import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {  registerPortalUserTpo } from '../services/apiclient';
import { toast } from "sonner";
import { Eye, EyeOff } from 'lucide-react';

// ✅ Zod Schema without role & manager
const newUserSchema = z.object({
  username: z.string().min(1, "Username is required"),
  email: z.string().email("Valid email is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  location: z.string().min(1, "Location is required"),
  phone: z.string().regex(/^\d{10}$/, "Valid 10-digit phone number is required"),
});

type NewUser = z.infer<typeof newUserSchema>;

export const FloatingActions = ({ userAdded, setUserAdded }: { userAdded: boolean, setUserAdded: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NewUser>({
    resolver: zodResolver(newUserSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      location: "",
      phone: "",
    },
  });

  // ✅ Submit handler
  const onSubmit = async (data: NewUser) => {
    try {
      setIsLoading(true);
      const response = await registerPortalUserTpo({ ...data });
      if (response.status === "success") {
        toast.success(response.message || "TPO added successfully!");
        reset();
        setUserAdded(true);
        setOpen(false);
      } else {
        throw new Error(response.message || "Unknown error occurred");
      }
    } catch (error: any) {
      setUserAdded(false);
      const errorMessage = error.response?.data?.message || error.message || "An unexpected error occurred";
      console.error("Failed to register portal user:", errorMessage);
      toast.error(`Failed to add user: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    reset();
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="default" size="icon" className="px-4 hover:bg-[#EC6E51]/90" style={{ padding: "0px 40px" }}>
            Add TPO
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold"> Add TPO</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex flex-col gap-4">
                {/* Username */}
                <div className="relative">
                  <Input
                    {...register("username")}
                    placeholder=" "
                    className="peer h-10 w-full border border-gray-300 text-gray-900 placeholder-transparent
                    focus:outline-none focus:ring-0 focus:shadow-none focus:border-2 focus:border-[#EC6E51]"
                  />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all
                    peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">
                    Username
                  </Label>
                  {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
                </div>

                {/* Password with Visibility Toggle */}
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    placeholder=" "
                    className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]"
                  />
                  <div
                    className="absolute right-3 top-2.5 text-gray-500 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </div>
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all
                    peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">
                    Password
                  </Label>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                {/* Location */}
                <div className="relative">
                  <Input
                    {...register("location")}
                    placeholder=" "
                    className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]"
                  />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all
                    peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">
                    Location
                  </Label>
                  {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location.message}</p>}
                </div>

                {/* Phone */}
                <div className="relative">
                  <Input
                    type="tel"
                    {...register("phone")}
                    placeholder=" "
                    className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]"
                  />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all
                    peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">
                    Phone
                  </Label>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>

                {/* Email */}
                <div className="relative">
                  <Input
                    type="email"
                    {...register("email")}
                    placeholder=" "
                    className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]"
                  />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all
                    peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                    peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">
                    Email
                  </Label>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <DialogPrimitive.Close>
                <Button
                  variant="outline"
                  onClick={handleDialogClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </DialogPrimitive.Close>
              <Button type="submit" disabled={isSubmitting || isLoading}>
                {isSubmitting || isLoading ? "Adding..." : "Add TPO"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Custom CSS for animations
const styles = `
@keyframes slide-in {
    0% { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
}
.animate-slide-in {
    animation: slide-in 0.3s ease-in-out forwards;
}
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);